/**
 * useReport.js
 * Generic data-fetching hook for report endpoints.
 * Handles loading, error, and auto-refresh on param changes.
 *
 * Usage:
 *   const { data, loading, error, refresh } = useReport(api.getSales, { period: '30d', page: 1 });
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * @param {Function} fetchFn  - api.getSales / api.getInventory etc.
 * @param {object}   params   - query params object; re-fetches whenever this changes
 * @returns {{ data, loading, error, refresh }}
 */
export function useReport(fetchFn, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialize params to detect changes without deep equality
  const paramsKey = JSON.stringify(params);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFn(params);
      setData(result);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchFn, paramsKey]);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  return { data, loading, error, refresh: load };
}
